import { ReactNode, createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';

import useAddMutation from 'src/compass/shared/hooks/reactQuery/useAddMutation';

interface IRenderedDataObject {
  html: string;
  docUrl?: string;
}

/*
  An optional render cache for SCE data objects, scoped to whatever mounts the provider.

  Rendering one is a POST that runs the whole SCE list query server-side, so re-fetching an
  identical table is pure latency.  Without a cache the Batch Send dialog re-renders the same table
  every time a row's message is toggled between the default and an override, and again each time the
  user returns to a row they have already looked at.

  Deliberately a context rather than a module-level Map: the cache lives exactly as long as the
  component that provides it, so a dialog's worth of renders is discarded when the dialog closes and
  a later dialog cannot show data fetched before an edit.  Consumers without a provider (the
  single-send compose, the template editor) get today's behaviour - always fetch.
*/
const DataObjectCacheContext = createContext<Map<string, Promise<any>> | null>(null);

const useDataObjectCache = () => useContext(DataObjectCacheContext);

/*
  Fetch a data object through the cache, de-duplicating requests that are still in flight.

  The cache holds the PROMISE, not the result.  Two things routinely ask for the same table in the
  same tick - the read-only preview and the row's (always-mounted, hidden) override editor - and a
  cache of finished results would have both miss, both fetch, and the second overwrite the first.
  Storing the promise means the second caller waits on the first caller's request.

  A rejected request is evicted so the next mount retries rather than replaying the failure.
*/
const getRenderedDataObject = <T,>(
  cache: Map<string, Promise<any>> | null,
  key: string,
  fetcher: () => Promise<T>
): Promise<T> => {
  if (!cache) return fetcher();

  const inFlight = cache.get(key);
  if (inFlight) return inFlight as Promise<T>;

  const promise = fetcher().catch((error) => {
    cache.delete(key);
    throw error;
  });

  cache.set(key, promise);

  return promise;
};

/*
  The whole request is the key: params, layout and the stamped insuredName all change the table.

  Keys are sorted so the string depends on the request's CONTENT and not on how the object was
  built.  The same table reaches this cache two ways - parsed out of the body's JSON for the
  read-only preview, and spread out of a Lexical node for the editor - and those two produce the
  same fields in different orders, which a plain stringify would treat as two different tables.
*/
const getDataObjectCacheKey = (dataObject: unknown) =>
  JSON.stringify(dataObject, (_key, value) =>
    value && typeof value === 'object' && !Array.isArray(value)
      ? Object.keys(value)
          .sort()
          .reduce<Record<string, unknown>>((sorted, key) => {
            sorted[key] = value[key];
            return sorted;
          }, {})
      : value
  );

/*
  Render one data object to its table HTML, through the cache when a provider is mounted.

  The cancellation, error and cache-key discipline live here rather than in each consumer: the
  editor's decorator and the read-only batch preview both need exactly this, and when it was written
  out twice the two copies immediately disagreed on details that are easy to get subtly wrong.
*/
export function useRenderedDataObject(renderDataObjectUrl: string, dataObject: any) {
  const { mutateAsync } = useAddMutation({ url: renderDataObjectUrl, showLoader: false });

  const cache = useDataObjectCache();
  // Memoized: the key walks and re-sorts the whole request, and the editor's decorator re-renders
  // on every keystroke - but the value is only ever an effect dependency.
  const cacheKey = useMemo(() => getDataObjectCacheKey(dataObject), [dataObject]);

  const [result, setResult] = useState<IRenderedDataObject | undefined>();

  useEffect(() => {
    let cancelled = false;

    setResult(undefined);

    getRenderedDataObject(cache, cacheKey, () => mutateAsync(dataObject))
      .then((rendered) => {
        // A layout change (or an unmount) can land first; the cache keeps the result either way,
        // but showing it here would be showing the wrong table.
        if (!cancelled) setResult(rendered as IRenderedDataObject);
      })
      .catch(() => {
        // The mutation surfaces its own error; caught here only so an unrenderable table does not
        // become an unhandled rejection.
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cacheKey]);

  return result;
}

export function DataObjectCacheProvider({ children }: { children: ReactNode }) {
  const cache = useRef(new Map<string, Promise<any>>()).current;

  return (
    <DataObjectCacheContext.Provider value={cache}>{children}</DataObjectCacheContext.Provider>
  );
}
